// @ts-check
import {
	resolveFormType,
	TRACKING_PARAMETERS,
} from "./analytics-parameters.js";

/**
 * Push a `form_success` event to the GTM dataLayer.
 *
 * GTM/GA4 itself is not loaded by the component library. The container ID
 * (`GTM-P5WJBPBS` for finstral.com since FINSTRAL-2218) is set per domain in
 * the admin UI (`/admin/config/system/gtm`) and injected into every
 * page by the contrib `gtm` module
 * (`apps/drupal/web/modules/contrib/gtm/gtm.module`), which is what creates
 * the global `window.dataLayer` array and loads `gtm.js`. From here we only
 * push events onto that array; the GTM container is responsible for
 * forwarding them to GA4 according to the user's consent state (Consent
 * Mode v2).
 *
 * Safe to call in environments without GTM (preview, Miyagi, unit tests):
 * `window.dataLayer` is created on demand and the push is a no-op as far
 * as analytics is concerned.
 *
 * ## Payload shape
 *
 * One flat `dataLayer.push` per successful submission, following the form
 * type's example in Finstral's Data Layer specification:
 *
 *     {
 *         event: "form_success",
 *         form_id: "form_home_visit",
 *         form_name: "Schedule a Home Visit",
 *         lead_type: "On-Site Appointment",
 *         form_destination: "/de/partner-form/confirmation/on-site-appointment",
 *         contact_type: "onsite_appointment",
 *         topic: ["windows", "front_doors"],
 *         weekdays: ["monday"],
 *         time_of_day: ["morning"],
 *         marketing_consent: true,
 *         partner_consent: false,
 *     }
 *
 * `form_id` and `form_name` come from `FORM_TYPES` (analytics-parameters.js),
 * resolved from the component and, for components serving several form
 * types, the variant.
 * @param {object} params
 * @param {string} params.form        - The form's component (folder name),
 *                                       e.g. `dealer-request-form`. Resolved
 *                                       to `form_id` and `form_name`; an
 *                                       unknown component pushes nothing.
 * @param {string} [params.variant]   - The variant for components serving
 *                                       several form types (see
 *                                       `FORM_TYPES`).
 * @param {string} [params.leadType]  - Human-readable English label
 *                                       describing the user's selected
 *                                       intent (e.g. "Home Visit"). Omitted
 *                                       from the payload when not provided.
 * @param {string} [params.formDestination] - The thank-you page URL the form
 *                                       redirects to. Reported as its
 *                                       pathname (`form_destination`). Omit it
 *                                       for inline confirmations that do not
 *                                       navigate.
 * @param {Record<string, string | string[] | boolean | undefined>} [params.parameters]
 *                                     - Additional non-PII parameters, already
 *                                       in their standardized form (see
 *                                       `getTrackingParameters` and
 *                                       `getConsentParameters`). `undefined`,
 *                                       empty strings and empty arrays are
 *                                       dropped.
 */
export const trackFormSuccess = ({
	form,
	variant,
	leadType,
	formDestination,
	parameters = {},
}) => {
	if (typeof window === "undefined") {
		return;
	}

	const formType = resolveFormType(form, variant);
	if (!formType) {
		return;
	}

	window.dataLayer = window.dataLayer || [];

	/** @type {Record<string, string | string[] | boolean>} */
	const eventParameters = {
		form_id: formType.form_id,
		form_name: formType.form_name,
	};
	if (leadType) {
		eventParameters.lead_type = leadType;
	}

	const destination = toPathname(formDestination);
	if (destination) {
		eventParameters.form_destination = destination;
	}

	for (const [key, value] of Object.entries(parameters)) {
		if (RESERVED_KEYS.has(key)) continue;
		if (value === undefined || value === "") continue;
		if (Array.isArray(value) && value.length === 0) continue;
		eventParameters[key] = Array.isArray(value) ? [...value] : value;
	}

	window.dataLayer.push({ event: "form_success", ...eventParameters });
};

/**
 * Keys owned by `trackFormSuccess` itself; `parameters` cannot override them.
 */
const RESERVED_KEYS = new Set([
	"event",
	"form_id",
	"form_name",
	"lead_type",
	"form_destination",
]);

/**
 * Resolve a (possibly relative) URL to its pathname. The query string and
 * hash are dropped: they can carry tokens or campaign data that do not
 * belong in a page-type dimension.
 * @param {string | undefined} url
 * @returns {string | undefined}
 */
const toPathname = (url) => {
	if (!url) return undefined;
	try {
		return new URL(url, window.location.href).pathname;
	} catch {
		return undefined;
	}
};

/**
 * The value an input reports to analytics: its `data-track-value` when set
 * (used where `value` is a translated label), otherwise its `value`.
 * @param {HTMLInputElement | HTMLOptionElement} element
 * @returns {string}
 */
const trackValueOf = (element) => element.dataset.trackValue ?? element.value;

/**
 * Whether `FormData` would skip the control: disabled itself, or inside a
 * disabled `<fieldset>` (which `control.disabled` does not report).
 * @param {HTMLInputElement | HTMLSelectElement} control
 * @returns {boolean}
 */
const isDisabled = (control) =>
	control.disabled || control.closest("fieldset[disabled]") !== null;

/**
 * The values a named field currently submits, as analytics source values.
 * Mirrors `FormData`: disabled controls, unchecked checkboxes/radios and
 * unselected options contribute nothing.
 * @param {HTMLFormElement} form
 * @param {string} fieldName
 * @returns {string[]}
 */
const getSubmittedTrackValues = (form, fieldName) => {
	/** @type {string[]} */
	const values = [];
	const controls = form.querySelectorAll(`[name="${CSS.escape(fieldName)}"]`);

	for (const control of controls) {
		if (control instanceof HTMLSelectElement) {
			if (isDisabled(control)) continue;
			for (const option of control.selectedOptions) {
				values.push(trackValueOf(option));
			}
		} else if (control instanceof HTMLInputElement) {
			if (isDisabled(control)) continue;
			if (
				(control.type === "checkbox" || control.type === "radio") &&
				!control.checked
			) {
				continue;
			}
			values.push(trackValueOf(control));
		}
	}

	return values;
};

/**
 * Read categorical fields from a form and translate them to the standardized
 * values of the Data Layer specification (`TRACKING_PARAMETERS`).
 *
 * Call it in the success branch, right before `trackFormSuccess`, so it
 * reports what was actually submitted. Values without a mapping are dropped
 * rather than reported raw, so a translated label or a free-text value can
 * never reach the dataLayer through this path.
 * @example
 *     getTrackingParameters(form, { topic: "interest", weekdays: "preferredDay" })
 *     // → { topic: ["windows", "front_doors"], weekdays: ["monday"] }
 * @param {HTMLFormElement | null} form
 * @param {Record<string, string>} fields - dataLayer parameter → form field
 *                                           `name`.
 * @returns {Record<string, string | string[]>}
 */
export const getTrackingParameters = (form, fields) => {
	/** @type {Record<string, string | string[]>} */
	const parameters = {};
	if (!form) return parameters;

	for (const [parameter, fieldName] of Object.entries(fields)) {
		const definition = TRACKING_PARAMETERS[parameter];
		if (!definition) continue;

		const values = getSubmittedTrackValues(form, fieldName)
			.map((value) => definition.values[value])
			.filter((value) => value !== undefined);
		const unique = [...new Set(values)];

		if (unique.length === 0) continue;
		parameters[parameter] = definition.multiple ? unique : unique[0];
	}

	return parameters;
};

/**
 * The consent checkboxes of the shared privacy section, as the booleans the
 * Data Layer specification defines. A checkbox missing from the form is
 * omitted instead of being reported as `false`.
 * @param {HTMLFormElement | null} form
 * @returns {{ marketing_consent?: boolean, partner_consent?: boolean }}
 */
export const getConsentParameters = (form) => {
	/** @type {{ marketing_consent?: boolean, partner_consent?: boolean }} */
	const parameters = {};
	if (!form) return parameters;
	const marketing = form.querySelector('input[name="consentToContact"]');
	const partner = form.querySelector('input[name="consentToContactPartner"]');

	if (marketing instanceof HTMLInputElement) {
		parameters.marketing_consent = marketing.checked;
	}
	if (partner instanceof HTMLInputElement) {
		parameters.partner_consent = partner.checked;
	}

	return parameters;
};
